import { ApiService } from '~/services/api/api'
import type {
  OnboardingProfile,
  User,
  UserWithOrganizations,
  UserWithWorkspaceSummaries,
  UserWorkspaceMembershipDetail,
} from '~/types/User'
import type { PaginatedResponse } from '~/types/PaginatedResponse'

export class UserService {
  private readonly apiService = new ApiService()

  // GET /users is now paginated ({ meta, data }) and returns lightweight
  // UserSummary rows (with `isSuper`, but no `workspaces`) for active users
  // only — deactivated accounts moved to GET /deactivated-users, consumed as a
  // UsersPanel server source. The legacy system-admin users table + the id→name
  // resolver cells expect the old flat user list and consume it as a complete
  // id→user directory, so we page through every page (capped at MAX_PAGE_LIMIT
  // = 100 per request) and surface the flattened array — otherwise installs
  // with >100 active users show `[Not Found]` labels and stale joins.
  // `workspaces` is absent from the summary and degrades to undefined until
  // that table is redesigned (TRV-973).
  async getUsers(queryParameters: Record<string, string> = {}) {
    const users: UserWithWorkspaceSummaries[] = []
    let page = 1
    while (true) {
      const response = await this.apiService.get<
        PaginatedResponse<UserWithWorkspaceSummaries>
      >('/users', {
        params: { limit: '100', ...queryParameters, page: String(page) },
      })
      if (response.error) {
        return { data: null, error: response.error }
      }
      users.push(...(response.data?.data ?? []))
      if (!response.data?.meta.hasNextPage) {
        break
      }
      page += 1
    }
    return { data: users, error: null }
  }

  // Bootstrap the authenticated user (resolved from the JWT). Returns the flat
  // user profile plus the organizations they belong to.
  async getMe() {
    const response =
      await this.apiService.get<UserWithOrganizations>('/users/me')
    return response
  }

  async getUserById(userId: string) {
    const response = await this.apiService.get<User>(`/users/${userId}`)
    return response
  }

  // A user's workspace memberships enriched with workspace title, owning org
  // name, role and inviter. Unscoped spans every org; pass organizationId to
  // narrow to one org's workspaces.
  async getUserWorkspaceMemberships(userId: string, organizationId?: string) {
    const response = await this.apiService.get<UserWorkspaceMembershipDetail[]>(
      `/users/${userId}/workspace-memberships`,
      { params: organizationId ? { organizationId } : {} },
    )
    return response
  }

  // Deactivates a user (super-only). Returns the deactivated user id.
  async deactivateUser(userId: string) {
    const response = await this.apiService.post<{ id: string }, undefined>(
      `/users/${userId}/deactivate`,
      undefined,
    )
    return response
  }

  // Reactivates a deactivated user (super-only). Returns the reactivated id.
  async reactivateUser(userId: string) {
    const response = await this.apiService.post<{ id: string }, undefined>(
      `/users/${userId}/reactivate`,
      undefined,
    )
    return response
  }

  async updateUserById(userId: string, body: Partial<User>) {
    const response = await this.apiService.patch<
      UserWithWorkspaceSummaries,
      Partial<User>
    >(`/users/${userId}`, body)
    return response
  }

  async createUser(body: Partial<User>) {
    const response = await this.apiService.post<
      UserWithWorkspaceSummaries,
      Partial<User>
    >('/users', body)
    return response
  }

  async inviteUser(email: string) {
    const response = await this.apiService.post<
      UserWithWorkspaceSummaries,
      { email: string }
    >('/users/sign-up', { email })
    return response
  }

  // Triggers a WorkOS password-reset for the user. The reset email is dispatched
  // server-side via the `password_reset.created` webhook; the endpoint itself
  // returns 204 with no body.
  async requestPasswordReset(userId: string) {
    const response = await this.apiService.post<undefined, undefined>(
      `/users/${userId}/password-reset`,
      undefined,
    )
    return response
  }

  async completeOnboarding(userId: string, profile: OnboardingProfile) {
    const response = await this.apiService.post<
      UserWithWorkspaceSummaries,
      OnboardingProfile
    >(`/users/${userId}/onboarding`, profile)
    return response
  }

  async uploadAvatar(userId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file, file.name)

    const response = await this.apiService.post<
      { avatarUrl: string },
      FormData
    >(`/users/${userId}/avatar`, formData)
    return response
  }

  async deleteAvatar(userId: string) {
    const response = await this.apiService.delete<{ avatarUrl: null }>(
      `/users/${userId}/avatar`,
    )
    return response
  }
}
